import * as Sentry from "@sentry/react";
import trim from "lodash/trim";
import queryString from "query-string";
import { applyMiddleware, createStore, compose, Reducer, Store } from "redux";
import { ApiError, apiMiddleware, RSAA, getJSON } from "redux-api-middleware";
import { createLogger } from "redux-logger";
import thunk from "redux-thunk";

import { attemptRefresh } from "../shared/actions/auth.actions";

import { API_MODULES as modules } from "../shared/api/api.constants";

import { logout } from "../common/services/auth/Auth.service";
import { JwtService } from "../shared/services/Jwt.service";
import Localization from "../shared/services/Localization.service";

type BeOpts = Record<string, string>;

type ApiConfig = {
  beOpts: BeOpts;
  farmId: string | undefined;
  handleError?: (res: Response, displayErrorModal?: unknown) => void;
  displayErrorModal?: unknown;
};

type RsaaType =
  | string
  | {
      type: string;
      payload?: (
        action: unknown,
        state: unknown,
        res: Response | undefined,
      ) => unknown;
      meta?: unknown;
    };

/*
 Middleware for RSAA actions
 */
const setupHeaders = (
  headers: Record<string, string> | undefined,
  disableAuthHeader: boolean | undefined,
  noContentType: boolean | undefined,
): Record<string, string> => {
  const newHeaders: Record<string, string> = {
    "Content-Type": "application/json;charset=UTF-8",
    Accept: "application/json, text/plain, */*",
    ...headers,
  };
  if (noContentType) {
    delete newHeaders["Content-Type"];
  }

  const authHeader = getAuthorizationHeader();
  if (authHeader && !disableAuthHeader) {
    newHeaders.Authorization = authHeader;
  }

  return newHeaders;
};

const enrichMeta = (
  meta: unknown,
  metaParams: unknown[],
  enrichment: Record<string, unknown> | null,
): unknown => ({
  ...(meta && typeof meta === "function" ? meta(...metaParams) : meta),
  ...enrichment,
});

const setupContext = (
  context: string | undefined,
  types: RsaaType[],
): RsaaType[] => {
  if (context) {
    return types.map((type) => ({
      ...(typeof type === "object" ? type : { type }),
      meta: (action: unknown, state: unknown, res: Response | undefined) =>
        enrichMeta((type as { meta?: unknown }).meta, [action, state, res], {
          context,
        }),
    }));
  }
  return types;
};

const setupResponseHandlers = (
  types: RsaaType[],
  apiConfig: ApiConfig,
): RsaaType[] =>
  types.map((type, index) => ({
    ...(typeof type === "object" ? type : { type }),
    payload: (action: unknown, state: unknown, res: Response | undefined) => {
      if (res) {
        const resClone = res.clone();
        if (index === 2) {
          return getJSON(resClone).then(
            (json) => new ApiError(res.status, res.statusText, json),
          );
        }
        return (
          type as {
            payload?: (
              action: unknown,
              state: unknown,
              res: Response,
            ) => unknown;
          }
        ).payload
          ? (
              type as {
                payload: (
                  action: unknown,
                  state: unknown,
                  res: Response,
                ) => unknown;
              }
            ).payload(action, state, res)
          : getJSON(resClone);
      }
      return (
        type as {
          payload?: (
            action: unknown,
            state: unknown,
            res: Response | undefined,
          ) => unknown;
        }
      ).payload
        ? (
            type as {
              payload: (
                action: unknown,
                state: unknown,
                res: Response | undefined,
              ) => unknown;
            }
          ).payload(action, state, res)
        : undefined;
    },
    meta: (action: unknown, state: unknown, res: Response | undefined) => {
      let enrichment: Record<string, unknown> | null = null;
      if (res) {
        if (apiConfig.handleError) {
          apiConfig.handleError(res, apiConfig.displayErrorModal);
        }

        enrichment = {
          headers: res.headers,
        };
      }

      return enrichMeta(
        (type as { meta?: unknown }).meta,
        [action, state, res],
        enrichment,
      );
    },
  }));

const setupEndpoint = (
  endpoint: string,
  mod: string,
  proxy: boolean | undefined,
  apiConfig: ApiConfig,
): string => {
  const { beOpts, farmId } = apiConfig;
  const params = {
    farmIds: proxy || mod === modules.GATEWAY ? undefined : farmId || undefined,
    language:
      proxy || mod === modules.GATEWAY ? undefined : getLangKey() || undefined,
  };

  return `${getBaseUrl(mod, mapRouteConfig(beOpts))}/${trimParamsWhitespace(
    `${endpoint}${queryString.stringify(params)}`,
  )}`;
};

export const trimParamsWhitespace = (endpoint: string): string => {
  try {
    const [path, query] = endpoint.split("?");
    const params = queryString.parse(query);
    Object.keys(params).forEach((key) => {
      params[key] = trim(params[key] as string);
    });
    return `${path}${query ? `?${queryString.stringify(params)}` : ""}`;
  } catch {
    return endpoint;
  }
};

function getBaseUrl(mod: string, opts: Record<string, string>): string {
  if (!opts[mod]) {
    throw new Error(
      "Unknown API module requried. Please define `module` property of RSAA action.",
    );
  }
  return opts[mod];
}

function getAuthorizationHeader(): string | null {
  const token = JwtService.getToken();
  return JwtService.hasValidToken() ? `Bearer ${token!.id_token}` : null;
}

function mapRouteConfig(config: BeOpts): Record<string, string> {
  return {
    [modules.GATEWAY]: config.gatewayUrl,
    [modules.AGROEVIDENCE]: config.agroevidenceUrl,
    [modules.IOT]: config.iotUrl,
    [modules.AUTOMATION]: config.automationUrl,
    [modules.STORES]: config.storesUrl,
    [modules.SENTINEL]: config.sentinelUrl,
    [modules.IRRIGATION]: config.irrigationUrl,
    [modules.TELEMATICS]: config.telematicsUrl,
  };
}

const cfApiMiddleware =
  (apiConfig: ApiConfig) =>
  (store: Store) =>
  // oxlint-disable-next-line typescript/no-explicit-any
  (next: (action: any) => any) =>
  // oxlint-disable-next-line typescript/no-explicit-any
  (action: any) => {
    const rsaa = action ? action[RSAA] : action;
    if (rsaa && rsaa.module) {
      const { context, module, noAuthHeader, noContentType, proxy } = rsaa;
      delete rsaa.proxy;
      delete rsaa.module;
      delete rsaa.noAuthHeader;
      delete rsaa.noContentType;
      delete rsaa.context;

      rsaa.endpoint = setupEndpoint(rsaa.endpoint, module, proxy, apiConfig);

      if (!proxy) {
        rsaa.headers = setupHeaders(rsaa.headers, noAuthHeader, noContentType);

        rsaa.types = setupContext(context, rsaa.types);

        rsaa.types = setupResponseHandlers(rsaa.types, apiConfig);

        return next(action).then(
          attemptRefresh({
            action,
            next,
            store,
            authHeaderSupplier: getAuthorizationHeader,
            failure: logout,
          }),
        );
      }
    }

    return action ? next(action) : action;
  };

function getLangKey(): string | undefined {
  let langKey;
  const language = Localization.getLang();

  const indexOfLangSeparator = language.indexOf("-");
  if (indexOfLangSeparator > 0) {
    langKey = language.substring(0, indexOfLangSeparator);
  }

  return langKey;
}

function init(
  apiConfig: ApiConfig | null,
  reducer: () => Reducer,
  preloadedState: Record<string, unknown> | null = null,
  loggerSetup: Record<string, unknown> = {},
  environment: string = "production",
): Store {
  let store: Store;

  // oxlint-disable-next-line typescript/no-explicit-any
  const middleware: any[] = [thunk];
  if (apiConfig) {
    middleware.unshift(cfApiMiddleware(apiConfig), apiMiddleware);
  }
  if (process.env.NODE_ENV === "development") {
    middleware.push(createLogger(loggerSetup));
  }

  const sentryReduxEnhancer = Sentry.createReduxEnhancer();

  let enhancers = applyMiddleware(...middleware);
  if (environment !== "prod") {
    // oxlint-disable-next-line no-console
    console.info(`ENV:${environment} --> ReduxDevTools applied`);
    enhancers = compose(applyMiddleware(...middleware), sentryReduxEnhancer);
  } else {
    enhancers = compose(applyMiddleware(...middleware), sentryReduxEnhancer);
  }

  if (preloadedState) {
    store = createStore(reducer(), preloadedState, enhancers);
  } else {
    store = createStore(reducer(), enhancers);
  }

  return store;
}

export default init;
